import { createSlice } from "@reduxjs/toolkit";
import { MessageBarType } from "@fluentui/react";

import { TEXT } from "../../../shared/constants/texts";
import { EMAIL_THREAD_FORMAT, VIEW_FOLDER_AND_DOCUMENTS } from "../../../shared/constants/addToICIToggleView";

export const INITIAL_FORM_DATA = {
  selectedUploadType: "",
  selectedAssociation: null,
  newDocumentName: "",
  selectedFolderNewDocument: null,
  selectedFolderReplaceExisting: null,
  isLoadingAssociationInstances: false,
  associationInstances: [],
  showAssociationInstancesPopUp: false,
  isLoadingAssociationInstancesPopUp: false,
  associationInstancesPopUp: [],
  isLoadingFolderInstances: false,
  folderInstances: [],
  showFolderInstancesPopUp: false,
  isLoadingFolderInstancesPopUp: false,
  folderInstancesPopUp: [],
  replaceAssociationInstance: null,
  replaceAssociationInstancePopup: null,
  replaceFolderInstance: null,
  replaceFolderInstancePopup: null,
};

export const INITIAL_STATE = {
  // Email Thread Information
  singleEmailData: null,
  restUrlData: "",
  ewsUrlData: "",

  // Selected Email Thread Format
  selectedEmailThreadFormat: EMAIL_THREAD_FORMAT[0],

  // Selected View
  selectedViewFolderAndDocuments: VIEW_FOLDER_AND_DOCUMENTS[0],

  // Attachment Flag
  areAttachmentsIncluded: true,

  // Enable or Disable Advance Page
  isAdvancePageEnabled: false,

  // Fetch Association and Folder Data for Advance Page
  isFetchingAssociationAndFolder: true,

  // Attributes
  isFetchingAttributeData: false,
  attributeData: [],
  selectedAttributes: [],

  // Saved Searches
  isFetchingSavedSearchData: false,
  savedSearchData: [],
  selectedSavedSearch: null,

  // Agreements
  isFetchingAgreementData: false,
  agreementData: null,
  selectedAgreement: null,

  // Associations
  isFetchingAssociationData: false,
  associationData: [],
  selectedAssociation: null,

  // Association Instances
  isFetchingAssociationInstances: false,
  associationInstancesData: [],
  selectedAssociationInstance: null,

  // Folders
  isFetchingFolderData: false,
  folderData: null,
  selectedFolder: null,

  // Folder Instances
  isFetchingFolderInstances: false,
  folderInstancesData: [],
  selectedFolderInstance: null,

  // Drag and Drop Upload
  isUploadingDndAgreement: false,
  isUploadingDndAssociation: false,
  isUploadingDndFolder: false,
  isUploadingDndAssociationInstance: false,
  isUploadingDndFolderInstance: false,

  // Replace Existing
  isReplaceExistingPopupOpen: false,
  replaceExistingData: [],

  // MS Graph Tokens
  isFetchingMsGraphTokens: false,

  // Default Association
  defaultAssociationOfAgreement: null,

  // Id's for Folder Instances
  folderIdsForInstances: [],

  // Notifications
  showNotification: false,
  notificationType: null,
  notificationMessage: "",
  retryActionOnError: () => {},
  errMessage: "",
  correlationId: "",
  errorCode: "",

  // Single Email Thread Upload
  isUploadingSingleEmailThreadUpload: true,
  uploadSingleEmailThreadData: null,

  // Email Thread Form Data
  emailThreadFormData: { ...INITIAL_FORM_DATA },

  // Email Attachments Form Data
  emailAttachmentFormData: [],

  // Tooltip Attributes
  tooltipAttributes: [],

  // Single Email Upload
  isUploadingSingleEmailUpload: false,
  isUploadDoneSingleEmailUpload: false,

  // Agreement Version Advanced
  isMajorVersionConfigured: false,
  isMajorVersion: false,
  isVersionLabelsConfigured: false,
  versionLabels: [],
  selectedVersionLabel: null,
  majorVersionLocalizedString: TEXT.DEFAULT_MAJOR_VERSION_TEXT,
};

const addToIciSlice = createSlice({
  name: "add-to-ici",
  initialState: INITIAL_STATE,
  reducers: {
    // Reset for Unmounting Scenarios
    resetIndexAddToIci(state) {
      return {
        ...INITIAL_STATE,
        selectedEmailThreadFormat: state.selectedEmailThreadFormat,
        selectedViewFolderAndDocuments: state.selectedViewFolderAndDocuments,
        areAttachmentsIncluded: state.areAttachmentsIncluded,
        isAdvancePageEnabled: state.isAdvancePageEnabled,
        singleEmailData: state.singleEmailData,
        restUrlData: state.restUrlData,
        ewsUrlData: state.ewsUrlData,
        selectedAgreement: state.selectedAgreement,
        associationData: state.associationData,
        selectedAssociation: state.selectedAssociation,
        folderData: state.folderData,
        selectedFolder: state.selectedFolder,
        defaultAssociationOfAgreement: state.defaultAssociationOfAgreement,
      };
    },
    resetIndexSingleEmailUpload(state) {
      return {
        ...INITIAL_STATE,
        selectedEmailThreadFormat: state.selectedEmailThreadFormat,
        selectedViewFolderAndDocuments: state.selectedViewFolderAndDocuments,
        areAttachmentsIncluded: state.areAttachmentsIncluded,
        isAdvancePageEnabled: state.isAdvancePageEnabled,
        singleEmailData: state.singleEmailData,
        restUrlData: state.restUrlData,
        ewsUrlData: state.ewsUrlData,
      };
    },

    // Store Single Email Data
    storeSingleEmailData(state, action) {
      state.singleEmailData = action.payload;
    },

    // Store Email URL Data
    storeEmailUrlsData(state, action) {
      const { restUrl, ewsUrl } = action.payload;
      state.restUrlData = restUrl;
      state.ewsUrlData = ewsUrl;
    },

    // Set Email Thread Format
    setEmailThreadFormat(state, action) {
      state.selectedEmailThreadFormat = action.payload;
    },

    // Set View Folders and Documents
    setViewFolderAndDocuments(state, action) {
      state.selectedViewFolderAndDocuments = action.payload;
      state.selectedAgreement = null;
      state.associationData = [];
      state.selectedAssociation = null;
      state.folderData = null;
      state.selectedFolder = null;
      state.associationInstancesData = [];
      state.selectedAssociationInstance = null;
      state.folderInstancesData = [];
      state.selectedFolderInstance = null;
      state.defaultAssociationOfAgreement = null;
      state.folderIdsForInstances = [];
    },

    // Set Email Attachments Included
    setEmailAttachmentsIncluded(state, action) {
      state.areAttachmentsIncluded = action.payload;
    },

    // Enable or Disable Advance Page
    enableAdvancePage(state, action) {
      state.isAdvancePageEnabled = action.payload;
    },

    // Association and Folder Data for Advance Page
    startFetchingAssociationAndFolderForAdvancePage(state) {
      state.isFetchingAssociationAndFolder = true;
    },
    stopFetchingAssociationAndFolderForAdvancePage(state) {
      state.isFetchingAssociationAndFolder = false;
    },

    // Attributes
    startFetchingAttributes(state) {
      state.isFetchingAttributeData = true;
      state.attributeData = [];
      state.selectedAttributes = [];
    },
    storeAttributeData(state, action) {
      state.attributeData = action.payload;
      state.selectedAttributes = [];
      state.isFetchingAttributeData = false;
    },
    setSelectedAttributes(state, action) {
      state.selectedAttributes = action.payload;
    },

    // Saved Searches
    startFetchingSavedSearchData(state) {
      state.isFetchingSavedSearchData = true;
      state.savedSearchData = [];
      state.agreementData = null;
      state.selectedAgreement = null;
      state.associationData = [];
      state.selectedAssociation = null;
      state.folderData = null;
      state.selectedFolder = null;
      state.associationInstancesData = [];
      state.selectedAssociationInstance = null;
      state.folderInstancesData = [];
      state.selectedFolderInstance = null;
      state.defaultAssociationOfAgreement = null;
      state.folderIdsForInstances = [];
    },
    storeSavedSearchData(state, action) {
      const data = action.payload;
      state.savedSearchData = data;
      state.selectedSavedSearch = data && data.length > 0 ? data[0] : null;
      state.isFetchingSavedSearchData = false;
      state.selectedAttributes = [];
    },
    onSavedSearchChange(state, action) {
      state.selectedSavedSearch = action.payload;
      state.agreementData = null;
      state.selectedAgreement = null;
      state.associationData = [];
      state.selectedAssociation = null;
      state.folderData = null;
      state.selectedFolder = null;
      state.associationInstancesData = [];
      state.selectedAssociationInstance = null;
      state.folderInstancesData = [];
      state.selectedFolderInstance = null;
      state.defaultAssociationOfAgreement = null;
      state.folderIdsForInstances = [];
      state.selectedAttributes = [];
    },

    // Agreements
    startFetchingAgreements(state) {
      state.isFetchingAgreementData = true;
      state.agreementData = null;
      state.selectedAgreement = null;
      state.associationData = [];
      state.selectedAssociation = null;
      state.folderData = null;
      state.selectedFolder = null;
      state.associationInstancesData = [];
      state.selectedAssociationInstance = null;
      state.folderInstancesData = [];
      state.selectedFolderInstance = null;
      state.defaultAssociationOfAgreement = null;
      state.folderIdsForInstances = [];
    },
    storeAgreementData(state, action) {
      state.agreementData = action.payload;
      state.isFetchingAgreementData = false;
    },
    setSelectedAgreement(state, action) {
      state.selectedAgreement = action.payload;
      state.associationData = [];
      state.selectedAssociation = null;
      state.folderData = null;
      state.selectedFolder = null;
      state.associationInstancesData = [];
      state.selectedAssociationInstance = null;
      state.folderInstancesData = [];
      state.selectedFolderInstance = null;
      state.defaultAssociationOfAgreement = null;
      state.folderIdsForInstances = [];
    },

    // Associations
    startFetchingAssociations(state) {
      state.isFetchingAssociationData = true;
      state.associationData = [];
      state.selectedAssociation = null;
      state.associationInstancesData = [];
      state.selectedAssociationInstance = null;
    },
    storeAssociationData(state, action) {
      state.associationData = action.payload;
      state.isFetchingAssociationData = false;
    },
    setSelectedAssociation(state, action) {
      state.selectedAssociation = action.payload;
      state.associationInstancesData = [];
      state.selectedAssociationInstance = null;
    },

    // Association Instances
    startFetchingAssociationInstances(state) {
      state.isFetchingAssociationInstances = true;
    },
    storeAssociationInstancesData(state, action) {
      state.associationInstancesData = action.payload;
      state.isFetchingAssociationInstances = false;
    },
    setSelectedAssociationInstance(state, action) {
      state.selectedAssociationInstance = action.payload;
    },

    // Folders
    startFetchingFolders(state) {
      state.isFetchingFolderData = true;
      state.folderData = null;
      state.selectedFolder = null;
      state.folderInstancesData = [];
      state.selectedFolderInstance = null;
      state.defaultAssociationOfAgreement = null;
      state.folderIdsForInstances = [];
    },
    storeFolderData(state, action) {
      state.folderData = action.payload;
      state.isFetchingFolderData = false;
    },
    setSelectedFolder(state, action) {
      state.selectedFolder = action.payload;
    },

    // Folder Instances
    startFetchingFolderInstances(state) {
      state.isFetchingFolderInstances = true;
    },
    storeFolderInstancesData(state, action) {
      state.folderInstancesData = action.payload;
      state.isFetchingFolderInstances = false;
    },
    setSelectedFolderInstance(state, action) {
      state.selectedFolderInstance = action.payload;
    },

    // Uploading - DnD - Agreement
    startUploadingDndAgreement(state) {
      state.isUploadingDndAgreement = true;
    },
    stopUploadingDndAgreement(state) {
      state.selectedAgreement = null;
      state.associationData = [];
      state.selectedAssociation = null;
      state.folderData = null;
      state.selectedFolder = null;
      state.associationInstancesData = [];
      state.selectedAssociationInstance = null;
      state.folderInstancesData = [];
      state.selectedFolderInstance = null;
      state.folderIdsForInstances = [];
      state.isUploadingDndAgreement = false;
    },

    // Uploading - DnD - Association
    startUploadingDndAssociation(state) {
      state.isUploadingDndAssociation = true;
    },
    stopUploadingDndAssociation(state) {
      state.isUploadingDndAssociation = false;
      state.associationInstancesData = [];
      state.selectedAssociationInstance = null;
    },

    // Uploading - DnD - Association Instance
    startUploadingDndAssociationInstance(state) {
      state.isUploadingDndAssociationInstance = true;
    },
    stopUploadingDndAssociationInstance(state) {
      state.isUploadingDndAssociationInstance = false;
    },

    // Uploading - DnD - Folder
    startUploadingDndFolder(state) {
      state.isUploadingDndFolder = true;
    },
    stopUploadingDndFolder(state) {
      state.isUploadingDndFolder = false;
    },

    // Uploading - DnD - Folder Instance
    startUploadingDndFolderInstance(state) {
      state.isUploadingDndFolderInstance = true;
    },
    stopUploadingDndFolderInstance(state) {
      state.isUploadingDndFolderInstance = false;
    },

    // Replace Existing
    showReplaceExistingPopup(state) {
      state.isReplaceExistingPopupOpen = true;
    },
    hideReplaceExistingPopup(state) {
      state.isReplaceExistingPopupOpen = false;
    },
    storeReplaceExistingData(state, action) {
      state.replaceExistingData = action.payload;
    },

    // MS Graph Tokens
    startFetchingMsGraphTokens(state) {
      state.isFetchingMsGraphTokens = true;
    },
    stopFetchingMsGraphTokens(state) {
      state.isFetchingMsGraphTokens = false;
    },

    // Default Association
    setDefaultAssociationForAgreement(state, action) {
      state.defaultAssociationOfAgreement = action.payload;
    },

    // Store Id's for Folder Instances
    storeFolderIdsForInstances(state, action) {
      state.folderIdsForInstances = action.payload;
    },

    // Notifications
    showNotificationSuccess(state, action) {
      state.showNotification = true;
      state.notificationType = MessageBarType.success;
      state.notificationMessage = action.payload;
      state.retryActionOnError = () => {};
    },
    showNotificationError(state, action) {
      state.showNotification = true;
      state.notificationType = MessageBarType.error;
      state.notificationMessage = action.payload.message;
      state.errMessage = action.payload.errMessage;
      state.correlationId = action.payload.correlationId;
      state.errorCode = action.payload.errorCode;
      state.retryActionOnError = action.payload.retryFunction;
    },
    showNotificationWarning(state, action) {
      state.showNotification = true;
      state.notificationType = MessageBarType.warning;
      state.notificationMessage = action.payload;
      state.retryActionOnError = () => {};
    },
    showNotificationInfo(state, action) {
      state.showNotification = true;
      state.notificationType = MessageBarType.info;
      state.notificationMessage = action.payload;
      state.retryActionOnError = () => {};
    },
    hideNotification(state) {
      state.showNotification = false;
      state.notificationType = null;
      state.notificationMessage = "";
      state.retryActionOnError = () => {};
    },

    // Single Email Thread Upload
    storeUploadSingleEmailThreadData(state, action) {
      state.uploadSingleEmailThreadData = action.payload;
    },
    stopUploadingSingleEmailThread(state) {
      state.isUploadingSingleEmailThreadUpload = false;
    },

    // Email Thread Form Data
    storeEmailThreadFormData(state, action) {
      state.emailThreadFormData = action.payload;
    },

    // Email Attachment Form Data
    storeEmailAttachmentFormData(state, action) {
      state.emailAttachmentFormData = action.payload;
    },

    // Tooltip Attributes
    storeTooltipAttributes(state, action) {
      state.tooltipAttributes = action.payload;
    },

    // Single Email Upload
    showSingleEmailUpload(state) {
      state.isUploadingSingleEmailUpload = true;
    },

    hideSingleEmailUpload(state) {
      state.isUploadingSingleEmailUpload = false;
    },

    showIsUploadDoneSingleEmail(state) {
      state.isUploadDoneSingleEmailUpload = true;
    },

    // Agreement Version Advanced
    setIsMajorVersionConfigured(state, action) {
      state.isMajorVersionConfigured = action.payload;
    },
    setIsVersionLabelsConfigured(state, action) {
      state.isVersionLabelsConfigured = action.payload;
    },
    setIsMajorVersion(state, action) {
      state.isMajorVersion = action.payload;
    },
    setVersionLabels(state, action) {
      state.versionLabels = action.payload;
      state.selectedVersionLabel = null;
    },
    setSelectedVersionLabel(state, action) {
      state.selectedVersionLabel = action.payload;
    },
    setMajorVersionStringLocalized(state, action) {
      state.majorVersionLocalizedString = action.payload;
    },
  },
});

const { actions, reducer } = addToIciSlice;

export const {
  resetIndexAddToIci,
  resetIndexSingleEmailUpload,
  storeSingleEmailData,
  storeEmailUrlsData,
  setEmailThreadFormat,
  setViewFolderAndDocuments,
  setEmailAttachmentsIncluded,
  enableAdvancePage,
  startFetchingAssociationAndFolderForAdvancePage,
  stopFetchingAssociationAndFolderForAdvancePage,
  startFetchingSavedSearchData,
  storeSavedSearchData,
  onSavedSearchChange,
  startFetchingAgreements,
  storeAgreementData,
  setSelectedAgreement,
  startFetchingAssociations,
  storeAssociationData,
  setSelectedAssociation,
  startFetchingFolders,
  storeFolderData,
  setSelectedFolder,
  startUploadingDndAgreement,
  stopUploadingDndAgreement,
  startUploadingDndAssociation,
  stopUploadingDndAssociation,
  startUploadingDndFolder,
  stopUploadingDndFolder,
  showReplaceExistingPopup,
  hideReplaceExistingPopup,
  startFetchingAttributes,
  storeAttributeData,
  setSelectedAttributes,
  startFetchingAssociationInstances,
  storeAssociationInstancesData,
  setSelectedAssociationInstance,
  startUploadingDndAssociationInstance,
  stopUploadingDndAssociationInstance,
  startFetchingMsGraphTokens,
  stopFetchingMsGraphTokens,
  setDefaultAssociationForAgreement,
  startFetchingFolderInstances,
  storeFolderInstancesData,
  setSelectedFolderInstance,
  startUploadingDndFolderInstance,
  stopUploadingDndFolderInstance,
  storeFolderIdsForInstances,
  showNotificationError,
  hideNotification,
  showNotificationSuccess,
  showNotificationWarning,
  showNotificationInfo,
  storeUploadSingleEmailThreadData,
  stopUploadingSingleEmailThread,
  storeEmailThreadFormData,
  storeEmailAttachmentFormData,
  storeTooltipAttributes,
  storeReplaceExistingData,
  showSingleEmailUpload,
  hideSingleEmailUpload,
  showIsUploadDoneSingleEmail,
  setIsMajorVersion,
  setIsMajorVersionConfigured,
  setIsVersionLabelsConfigured,
  setMajorVersionStringLocalized,
  setSelectedVersionLabel,
  setVersionLabels,
} = actions;

export default reducer;
